import { computeSnoozeTargets, type SnoozeTarget } from "@app/core";
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { clampFloatingPosition } from "../../lib/floating-position.js";
import { motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { useDismiss } from "../../lib/useDismiss.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import type { I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore } from "../../stores/index.js";
import { DatePicker } from "../task-form/DatePicker.js";
import { TimePicker } from "../task-form/TimePicker.js";
import { toLocalDate } from "../task-form/time-helpers.js";

interface Props {
  x: number;
  y: number;
  /** Called with the chosen wake instant (ISO-8601 UTC). */
  onPick: (atIso: string) => void;
  onClose: () => void;
}

/** Floating preset menu for snoozing a task/occurrence. Presets resolve against
 *  the user's morning/evening anchor hours; "Custom…" swaps in the app's date +
 *  time pickers. Positioned + dismissed like {@link TaskContextMenu}. */
export function SnoozeMenu({ x, y, onPick, onClose }: Props) {
  const { t: tr } = useI18n();
  const morningHour = useSettingsStore((s) => s.snoozeMorningHour);
  const eveningHour = useSettingsStore((s) => s.snoozeEveningHour);
  const tf = useTimeFormat();
  const locale = useAppLocale();
  const ref = useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();
  const [custom, setCustom] = useState(false);
  const [dateStr, setDateStr] = useState(() => toLocalDate(new Date()));
  const [hm, setHm] = useState<{ h: number; m: number }>(() => ({ h: morningHour, m: 0 }));

  useDismiss(onClose, { capture: true }, ref);

  const targets = useMemo(
    () => computeSnoozeTargets(new Date(), { morningHour, eveningHour }),
    [morningHour, eveningHour],
  );

  const position = clampFloatingPosition(
    x, y, window.innerWidth, window.innerHeight, 224, custom ? 448 : 268,
  );
  const style: React.CSSProperties = {
    position: "fixed",
    ...position,
    zIndex: 9999,
  };

  function pick(atIso: string) {
    onClose();
    onPick(atIso);
  }

  function commitCustom() {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setHours(hm.h, hm.m, 0, 0);
    pick(d.toISOString());
  }

  return createPortal(
    <div ref={ref} style={style}>
    <motion.div
      initial={reduce ? false : { opacity: 0, scale: 0.96, y: -4 }}
      animate={reduce ? {} : { opacity: 1, scale: 1, y: 0 }}
      transition={springs.snappy}
      className="max-h-[calc(100vh-24px)] w-56 max-w-[calc(100vw-24px)] overflow-y-auto rounded-md border bg-card/95 text-card-foreground shadow-lg backdrop-blur-sm"
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
      role="menu"
    >
      {!custom ? (
        <div className="flex flex-col py-1">
          <p className="px-3 pb-1 pt-1.5 text-[calc(10px*var(--app-text-scale))] uppercase tracking-wide text-muted-foreground">{tr("snooze.until")}</p>
          {targets.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitem"
              onClick={() => pick(t.atIso)}
              className="flex items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
            >
              <span>{tr(snoozePresetKey(t.id))}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{fmtTarget(t, tf, locale)}</span>
            </button>
          ))}
          <div className="my-1 border-t" />
          <button
            type="button"
            role="menuitem"
            onClick={() => setCustom(true)}
            className="px-3 py-1.5 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
          >
            {tr("snooze.custom")}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 p-2">
          <DatePicker value={new Date(`${dateStr}T00:00:00`)} onSelect={(d) => setDateStr(toLocalDate(d))} />
          <TimePicker hour={hm.h} minute={hm.m} onChange={(h, m) => setHm({ h, m })} />
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent/80" onClick={() => setCustom(false)}>{tr("common.back")}</button>
            <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90" onClick={commitCustom}>{tr("snooze.commit")}</button>
          </div>
        </div>
      )}
    </motion.div>
    </div>,
    document.body,
  );
}

/** Same-day shows just the time; otherwise prepends the weekday. */
function fmtTarget(t: SnoozeTarget, tf: ReturnType<typeof useTimeFormat>, locale: string): string {
  const d = new Date(t.atIso);
  const time = tf.hm(d.getHours(), d.getMinutes());
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString(locale, { weekday: "short" })} ${time}`;
}

function snoozePresetKey(id: SnoozeTarget["id"]): I18nKey {
  if (id === "evening") return "snooze.preset.evening";
  if (id === "tomorrow") return "snooze.preset.tomorrow";
  if (id === "weekend") return "snooze.preset.weekend";
  return "snooze.preset.week";
}
